import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { ExamCreateDTO } from "../../schema/Exam/ExamCreateDTO";
import { ExamDTO } from "../../schema/Exam/ExamDTO";
import { ExamSummaryDTO } from "../../schema/Exam/ExamSummaryDTO";
import { QuestionCategoryCreateDTO } from "../../schema/Exam/QuestionCategoryCreateDTO";
import { QuestionCategoryDTO } from "../../schema/Exam/QuestionCategoryDTO";
import { QuestionCreateDTO } from "../../schema/Exam/QuestionCreateDTO";
import { QuestionDTO } from "../../schema/Exam/QuestionDTO";

@Injectable({
    providedIn: "root"
})
export class ApiExamService {
    private relativePath: string;

    constructor(private httpClient: HttpClient) {
        this.relativePath = `${environment.exams.url}/api/exam`;
    }

    public Get(id: string): Observable<ExamDTO> {
        return this.httpClient.get<ExamDTO>(`${this.relativePath}/${id}`);
    }

    public GetSummary(id: string): Observable<ExamSummaryDTO> {
        return this.httpClient.get<ExamSummaryDTO>(`${this.relativePath}/${id}/summary`);
    }

    public Create(exam: ExamCreateDTO): Observable<ExamDTO> {
        return this.httpClient.post<ExamDTO>(`${this.relativePath}`, exam);
    }

    public Update(id: string, exam: ExamDTO): Observable<any> {
        return this.httpClient.patch(`${this.relativePath}/${id}/settings`, exam);
    }

    public Delete(id: string): Observable<any> {
        return this.httpClient.delete(`${this.relativePath}/${id}`);
    }

    public CreateCategory(id: string, category: QuestionCategoryCreateDTO): Observable<ExamDTO> {
        return this.httpClient.post<ExamDTO>(`${this.relativePath}/${id}/category`, category);
    }

    public UpdateCategory(id: string, categoryId: string, QuestionCategory: QuestionCategoryDTO): Observable<any> {
        const body = [
            {
                value: QuestionCategory.name,
                path: "/name",
                op: "replace"
            },
            {
                value: QuestionCategory.description,
                path: "/description",
                op: "replace"
            },
            {
                value: QuestionCategory.weight,
                path: "/weight",
                op: "replace"
            },
            {
                value: QuestionCategory.numberOfQuestionsPerExam,
                path: "/numberOfQuestionsPerExam",
                op: "replace"
            }
        ];

        return this.httpClient.patch(`${this.relativePath}/${id}/category/${categoryId}`, body, {
            headers: {
                "Content-Type": "application/json-patch+json; charset=utf-8"
            }
        });
    }

    public UpdateCategoryQuestions(id: string, categoryId: string, questions: QuestionDTO[]): Observable<any> {
        return this.httpClient.patch(`${this.relativePath}/${id}/category/${categoryId}/questions`, questions);
    }

    public UpdateCategories(id: string, categories: QuestionCategoryDTO[]): Observable<any> {
        return this.httpClient.patch(`${this.relativePath}/${id}/categories`, categories);
    }

    public DeleteCategory(id: string, categoryId: string): Observable<any> {
        return this.httpClient.delete(`${this.relativePath}/${id}/category/${categoryId}`);
    }

    public CreateQuestion(id: string, categoryId: string, question: QuestionCreateDTO): Observable<ExamDTO> {
        return this.httpClient.post<ExamDTO>(`${this.relativePath}/${id}/category/${categoryId}/question`, question);
    }

    public UpdateQuestion(id: string, categoryId: string, questionId: string, question: QuestionDTO): Observable<any> {
        return this.httpClient.put(
            `${this.relativePath}/${id}/category/${categoryId}/question/${questionId}`,
            question
        );
    }

    public DeleteQuestion(id: string, categoryId: string, questionId: string): Observable<any> {
        return this.httpClient.delete(`${this.relativePath}/${id}/category/${categoryId}/question/${questionId}`);
    }

    public unlockUser(examId: string, userId: string, numberOfTrials?: number): Observable<any> {
        return this.httpClient.patch(`${this.relativePath}/${examId}/user/${userId}/unlock`, {
            NumberOfTrials: numberOfTrials
        });
    }

    public uploadMoodleXML(file: File, examId: string): Observable<any> {
        const formData = new FormData();
        formData.append("formFile", file);

        return this.httpClient.post(`${this.relativePath}/${examId}/import/moodle`, formData, {});
    }

    public getByCertificationId(certificationId: string): Observable<ExamSummaryDTO[]> {
        return this.httpClient.get<ExamSummaryDTO[]>(`${this.relativePath}/certification/${certificationId}`);
    }
}
